import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Coffee, DollarSign, ChevronRight, Download, RefreshCw, Edit3, Plus } from 'lucide-react';
import { MeetingStorage } from '../utils/meetingStorage';
import { authService, supabase } from '../utils/supabase';
import { meetingService } from '../utils/supabaseData';
import EnhancedMeetingCard from '../components/EnhancedMeetingCard';
import AvailableFavoritesCard from '../components/AvailableFavoritesCard';
import PullToRefresh from '../components/PullToRefresh';
import MeetingQuickViewModal from '../components/MeetingQuickViewModal';
import { customerFavoritesService } from '../utils/customerFavoritesService';
import { providerAvailabilityService } from '../utils/providerAvailabilityService';
import { preApprovedRepsService } from '../utils/preApprovedRepsService';
import { logger } from '../utils/logger';

const MeetingsView = ({ currentUser: passedCurrentUser, setCurrentView, setSelectedMeetingId, selectedMeetingDate, setSelectedMeetingDate, setSelectedCustomer }) => {
  const [viewType, setViewType] = useState('monthly'); // 'daily', 'weekly', 'monthly'
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [clickedDay, setClickedDay] = useState(null); // Track clicked day for filtering while keeping calendar visible
  const [meetings, setMeetings] = useState([]);
  const [currentUser, setCurrentUser] = useState(passedCurrentUser || null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('active-pending'); // 'all', 'confirmed', 'active-pending', 'pending', 'cancelled', 'completed'
  const [availableFavorites, setAvailableFavorites] = useState([]);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [blockedPeriods, setBlockedPeriods] = useState([]);

  // Meeting request modal state (for customers)
  const [selectedRequestMeeting, setSelectedRequestMeeting] = useState(null);
  const [selectedDayMeetings, setSelectedDayMeetings] = useState([]); // All meetings for the selected day
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [approvedReps, setApprovedReps] = useState([]);

  // Handler for "Add Meeting Manually" button
  const handleAddMeetingManually = (dateString = null) => {
    // Store the date for pre-selection in the add meeting view
    if (setSelectedMeetingDate) {
      setSelectedMeetingDate(dateString);
    }
    setCurrentView('add-meeting-manually');
  };

  // Load user meetings
  useEffect(() => {
    const loadMeetings = async () => {
      try {
        // Use passed currentUser if available (for impersonation), otherwise get from auth
        let effectiveCurrentUser = passedCurrentUser;
        if (!effectiveCurrentUser) {
          const { user, profile } = await authService.getEffectiveUser();
          if (user && profile) {
            effectiveCurrentUser = { ...user, ...profile };
          }
        }

        if (effectiveCurrentUser) {
          // Fix userType access - check both possible fields
          const userType = effectiveCurrentUser.userType || effectiveCurrentUser.user_type;

          setCurrentUser(effectiveCurrentUser);

          // Use Supabase for sales reps, localStorage for others (for now)
          if (userType === 'sales') {
            try {
              // Load meetings from Supabase for sales reps

              const supabaseMeetings = await meetingService.getAllMeetings(effectiveCurrentUser.id, userType);

              // Transform Supabase data to match UI expectations
              const transformedMeetings = await Promise.all(supabaseMeetings.map(async meeting => {
                // Extract customer name - now directly fetch since joins are removed
                let customerName = 'Healthcare Provider';
                let customerProfilePicture = null;

                // Always fetch customer data directly since we removed joins
                if (meeting.customer_id) {

                  try {

                    // Now try the specific lookup
                    const { data: customerData, error: fetchError } = await supabase
                      .from('user_profiles')
                      .select('first_name, last_name, id, profile_picture')
                      .eq('id', meeting.customer_id)
                      .single();

                    if (fetchError) {
                      logger.warn('MeetingsView: Customer not found in user_profiles', { errorCode: fetchError.code });
                    }

                    if (customerData && customerData.first_name) {
                      customerName = `${customerData.first_name} ${customerData.last_name || ''}`.trim();
                      customerProfilePicture = customerData.profile_picture;

                    } else {
                      logger.warn('MeetingsView: No customer data found for ID', { customerId: meeting.customer_id });
                      // Keep as 'Healthcare Provider' - this should not happen if customer creation works
                    }
                  } catch (fetchError) {
                    logger.error('MeetingsView: Exception fetching customer data', { error: fetchError.message });
                  }
                } else {
                  logger.warn('MeetingsView: No customer_id found for meeting', { meetingId: meeting.id });
                }

                return {
                  // Keep original fields and add UI-compatible ones
                  ...meeting,

                  // Transform field names to match UI expectations
                  date: meeting.meeting_date,
                  time: meeting.start_time,
                  salesRepId: meeting.sales_rep_id,
                  customerId: meeting.customer_id,
                  customer: customerName,
                  customer_profile_picture: customerProfilePicture,
                  restaurant: meeting.restaurant_name || meeting.restaurant || 'TBD',
                  status: meeting.status || 'pending',

                  // Ensure other common fields exist
                  orderTotal: meeting.actual_cost || meeting.estimated_cost || '0.00',
                  notes: meeting.meeting_notes || meeting.notes || ''
                };
              }));

              setMeetings(transformedMeetings || []);
            } catch (error) {
              logger.error('MeetingsView (Sales): Error loading Supabase meetings', { error: error.message });
              // Fallback to localStorage if Supabase fails
              const userMeetings = MeetingStorage.getMeetingsForUser(currentUser.id, userType);

              setMeetings(userMeetings);
            }
          } else {
            // Healthcare providers - try Supabase first, then localStorage fallback
            try {
              // Handle delegation: if office staff acting on behalf of physician, use physician's ID
              const effectiveUserId = effectiveCurrentUser.isActingOnBehalf
                ? effectiveCurrentUser.id  // Already set to physician's ID by getEffectiveUser
                : effectiveCurrentUser.id;

              logger.debug('MeetingsView: Loading meetings for customer', {
                userId: effectiveUserId,
                userType,
                isActingOnBehalf: effectiveCurrentUser.isActingOnBehalf,
                staffId: effectiveCurrentUser.staffId
              });

              // Use meetingService.getAllMeetings() which uses RPC function (bypasses RLS)
              const supabaseMeetings = await meetingService.getAllMeetings(effectiveUserId, 'customer');

              logger.debug('MeetingsView: Got meetings from Supabase via RPC', { count: supabaseMeetings.length });

              // Transform Supabase data to match UI expectations for customers
              const transformedMeetings = await Promise.all(supabaseMeetings.map(async meeting => {
                // For customers, we want to show the sales rep name, not customer name
                let salesRepName = 'Sales Representative';
                let salesRepProfilePicture = null;

                // Fetch sales rep data (use dynamic import like Dashboard.js for auth timing)
                if (meeting.sales_rep_id) {
                  try {
                    const { supabase: sb } = await import('../utils/supabase');
                    const { data: salesRepData, error: fetchError } = await sb
                      .from('user_profiles')
                      .select('first_name, last_name, id, profile_picture')
                      .eq('id', meeting.sales_rep_id)
                      .single();

                    if (fetchError) {
                      logger.warn('MeetingsView (Customer): Sales rep not found in user_profiles', { errorCode: fetchError.code });
                    }

                    if (salesRepData && salesRepData.first_name) {
                      salesRepName = `${salesRepData.first_name} ${salesRepData.last_name || ''}`.trim();
                      salesRepProfilePicture = salesRepData.profile_picture;
                    } else {
                      logger.warn('MeetingsView (Customer): No sales rep data found for ID', { salesRepId: meeting.sales_rep_id });
                    }
                  } catch (fetchError) {
                    logger.error('MeetingsView (Customer): Exception fetching sales rep data', { error: fetchError.message });
                  }
                } else if (meeting.external_sales_rep_name) {
                  // External rep - use stored name from meeting record
                  salesRepName = meeting.external_sales_rep_name;
                  logger.debug('MeetingsView (Customer): Using external sales rep name', { name: salesRepName });
                } else {
                  logger.warn('MeetingsView (Customer): No sales_rep_id or external rep found for meeting', { meetingId: meeting.id });
                }

                return {
                  // Keep original fields and add UI-compatible ones
                  ...meeting,

                  // Transform field names to match UI expectations
                  date: meeting.meeting_date,
                  time: meeting.start_time,
                  salesRepId: meeting.sales_rep_id,
                  customerId: meeting.customer_id,
                  salesRep: salesRepName,
                  salesRepName: salesRepName,
                  customer: salesRepName,
                  sales_rep_profile_picture: salesRepProfilePicture,
                  restaurant: meeting.restaurant_name || meeting.restaurant || 'TBD',
                  status: meeting.status || 'pending',

                  // Ensure other common fields exist
                  orderTotal: meeting.actual_cost || meeting.estimated_cost || '0.00',
                  notes: meeting.meeting_notes || meeting.notes || ''
                };
              }));

              logger.debug('MeetingsView: Transformed meetings', { count: transformedMeetings.length });
              setMeetings(transformedMeetings || []);
            } catch (error) {
              logger.error('MeetingsView (Customer): Error loading Supabase meetings', { error: error.message });
              logger.warn('MeetingsView: Falling back to localStorage');
              // Fallback to localStorage if Supabase fails
              const userMeetings = MeetingStorage.getMeetingsForUser(currentUser.id, userType);
              logger.debug('MeetingsView: Got meetings from localStorage', { count: userMeetings.length });
              setMeetings(userMeetings);
            }
          }
        } else {

        }
      } catch (error) {
        logger.error('MeetingsView: Error loading user data', { error: error.message });
      }
      setLoading(false);
    };

    loadMeetings();
  }, [passedCurrentUser]); // Reload when impersonated user changes

  // Load blocked periods for customers/office staff
  useEffect(() => {
    const loadBlockedPeriods = async () => {
      const userType = currentUser?.userType || currentUser?.user_type;

      // Only load blocked periods for customers and office staff
      if (!currentUser || (userType !== 'customer' && userType !== 'office_staff')) {
        setBlockedPeriods([]);
        return;
      }

      try {
        // Determine which provider ID to use
        let providerId = currentUser.id;

        // For office staff, use the physician they're delegating for
        if (userType === 'office_staff' && currentUser.delegating_for) {
          providerId = currentUser.delegating_for;
        }

        // Fetch blocked periods from all provider_availability records (location-specific and general)
        const { data, error } = await supabase
          .from('provider_availability')
          .select('blocked_periods, location_id')
          .eq('provider_id', providerId);

        if (error) {
          logger.error('MeetingsView: Error loading blocked periods', { error: error.message });
          return;
        }

        // Combine blocked periods from all records (location-specific and general)
        const allBlockedPeriods = [];
        if (data && data.length > 0) {
          data.forEach(record => {
            if (record.blocked_periods && record.blocked_periods.length > 0) {
              allBlockedPeriods.push(...record.blocked_periods);
            }
          });
        }

        if (allBlockedPeriods.length > 0) {
          setBlockedPeriods(allBlockedPeriods);
        } else {
          setBlockedPeriods([]);
        }
      } catch (error) {
        logger.error('MeetingsView: Exception loading blocked periods', { error: error.message });
        setBlockedPeriods([]);
      }
    };

    loadBlockedPeriods();
  }, [currentUser]);

  // Load approved reps for customers (for meeting request modal)
  useEffect(() => {
    const loadApprovedReps = async () => {
      const userType = currentUser?.userType || currentUser?.user_type;
      if (!currentUser || (userType !== 'customer' && userType !== 'office_staff')) {
        return;
      }

      try {
        const reps = await preApprovedRepsService.getPreApprovedReps(currentUser.id);
        const transformedReps = reps.map(rep => ({
          id: rep.salesRepId,
          name: rep.salesRepName || 'Unknown',
          email: rep.salesRepEmail || '',
          company: rep.salesRepCompany || 'Unknown Company'
        }));
        setApprovedReps(transformedReps);
      } catch (error) {
        logger.error('MeetingsView: Error loading approved reps', { error: error.message });
      }
    };

    loadApprovedReps();
  }, [currentUser]);

  // Handle navigation from Meeting Activity widget
  useEffect(() => {
    if (selectedMeetingDate) {
      const dateObj = typeof selectedMeetingDate === 'string'
        ? new Date(selectedMeetingDate)
        : selectedMeetingDate;

      setSelectedDate(dateObj);
      setViewType('daily');

      // Clear the selected date after navigating
      if (setSelectedMeetingDate) {
        setSelectedMeetingDate(null);
      }
    }
  }, [selectedMeetingDate, setSelectedMeetingDate]);

  // Sync clickedDay with selectedDate when switching to daily view
  useEffect(() => {
    if (viewType === 'daily') {
      // Set clickedDay to selectedDate for daily view
      setClickedDay(new Date(selectedDate));
    } else if (viewType !== 'monthly') {
      // Clear clickedDay for weekly and yearly views
      setClickedDay(null);
    }
    // For monthly view, keep clickedDay as is (user can manually click days)
  }, [viewType, selectedDate]);

  // Check availability of favorite providers when a day is clicked (sales reps only)
  useEffect(() => {
    const checkFavoriteAvailability = async () => {
      // Only check for sales reps
      const userType = currentUser?.userType || currentUser?.user_type;
      if (userType !== 'sales' || !clickedDay) {
        setAvailableFavorites([]);
        return;
      }

      // Only check if there are no meetings on the clicked day
      const clickedDayStart = new Date(clickedDay);
      clickedDayStart.setHours(0, 0, 0, 0);
      const clickedDayEnd = new Date(clickedDay);
      clickedDayEnd.setHours(23, 59, 59, 999);

      const hasMeetingsOnDay = meetings.some(meeting => {
        const meetingDate = parseLocalDate(meeting.date);
        meetingDate.setHours(0, 0, 0, 0);
        return meetingDate >= clickedDayStart && meetingDate <= clickedDayEnd;
      });

      if (hasMeetingsOnDay) {
        setAvailableFavorites([]);
        return;
      }

      // Load favorites and check their availability
      setLoadingAvailability(true);
      try {
        logger.debug('MeetingsView: Checking favorite provider availability', {
          userId: currentUser.id,
          clickedDay: clickedDay.toISOString()
        });

        // Get sales rep's favorite customers
        const favorites = await customerFavoritesService.getFavorites(currentUser.id);

        if (!favorites || favorites.length === 0) {
          logger.debug('MeetingsView: No favorites found');
          setAvailableFavorites([]);
          setLoadingAvailability(false);
          return;
        }

        // Get only registered provider IDs (those with accounts)
        const providerIds = favorites
          .filter(fav => fav.isRegistered)
          .map(fav => fav.customerId);

        if (providerIds.length === 0) {
          logger.debug('MeetingsView: No registered providers in favorites');
          setAvailableFavorites([]);
          setLoadingAvailability(false);
          return;
        }

        // Check availability for all favorite providers on the clicked day
        const availableProviders = await providerAvailabilityService.checkProvidersAvailability(
          providerIds,
          clickedDay
        );

        logger.debug('MeetingsView: Found available favorites', {
          count: availableProviders.length
        });

        setAvailableFavorites(availableProviders);
      } catch (error) {
        logger.error('MeetingsView: Error checking favorite availability', {
          error: error.message
        });
        setAvailableFavorites([]);
      } finally {
        setLoadingAvailability(false);
      }
    };

    checkFavoriteAvailability();
  }, [clickedDay, meetings, currentUser]);

  // Handle "Schedule Now" action - navigate to booking with pre-filled data
  const handleScheduleNow = (provider, timeSlot) => {
    logger.debug('MeetingsView: Scheduling meeting with provider', {
      providerId: provider.providerId,
      providerName: provider.providerName,
      timeSlot: timeSlot.displayLabel,
      hasLocation: !!timeSlot.locationData
    });

    // Set the selected customer for the booking view
    if (setSelectedCustomer) {
      setSelectedCustomer({
        id: provider.providerId,
        name: provider.providerName,
        specialty: provider.specialty,
        profile_picture: provider.profilePicture,
        location: provider.location, // General City, State format
        // Pre-fill the time slot
        preferredTime: timeSlot.startTime,
        // Pre-fill the date
        suggestedDate: clickedDay ? clickedDay.toISOString().split('T')[0] : null,
        // Pre-fill the specific practice location for this time slot
        suggestedLocation: timeSlot.locationData ? {
          location_id: timeSlot.locationData.id,
          practice_locations: {
            location_name: timeSlot.locationData.name,
            address: timeSlot.locationData.address,
            city: timeSlot.locationData.city,
            state: timeSlot.locationData.state,
            location_type: timeSlot.locationData.type
          }
        } : null
      });

      // Navigate to booking view
      setCurrentView('book-meeting');
    } else {
      logger.warn('MeetingsView: setSelectedCustomer not provided as prop');
    }
  };

  const refreshMeetings = async () => {
    setLoading(true);
    if (currentUser) {
      const userType = currentUser.userType || currentUser.user_type;

      if (userType === 'sales') {
        // Load meetings from Supabase for sales reps
        const supabaseMeetings = await meetingService.getAllMeetings(currentUser.id, userType);

        // Transform Supabase data to match UI expectations - fetch customer data like initial load
        const transformedMeetings = await Promise.all(supabaseMeetings.map(async meeting => {
          let customerName = 'Healthcare Provider';
          let customerProfilePicture = null;

          // Fetch customer data directly since we removed joins
          if (meeting.customer_id) {
            try {
              const { data: customerData, error: fetchError } = await supabase
                .from('user_profiles')
                .select('first_name, last_name, id, profile_picture')
                .eq('id', meeting.customer_id)
                .single();

              if (!fetchError && customerData && customerData.first_name) {
                customerName = `${customerData.first_name} ${customerData.last_name || ''}`.trim();
                customerProfilePicture = customerData.profile_picture;
              }
            } catch (fetchError) {
              logger.error('refreshMeetings: Exception fetching customer data', { error: fetchError.message });
            }
          }

          return {
            ...meeting,
            date: meeting.meeting_date,
            time: meeting.start_time,
            salesRepId: meeting.sales_rep_id,
            customerId: meeting.customer_id,
            customer: customerName,
            customer_profile_picture: customerProfilePicture,
            restaurant: meeting.restaurant_name || meeting.restaurant || 'TBD',
            status: meeting.status || 'pending',
            orderTotal: meeting.actual_cost || meeting.estimated_cost || '0.00',
            notes: meeting.meeting_notes || meeting.notes || ''
          };
        }));

        setMeetings(transformedMeetings);
      } else {
        // Healthcare providers - try Supabase first, then localStorage fallback
        try {
          const supabaseMeetings = await meetingService.getAllMeetings(currentUser.id, userType);

          // Transform Supabase data for customers (show sales rep name)
          const transformedMeetings = await Promise.all(supabaseMeetings.map(async meeting => {
            let salesRepName = 'Sales Representative';
            let salesRepProfilePicture = null;

            if (meeting.sales_rep_id) {
              try {
                const { data: salesRepData } = await supabase
                  .from('user_profiles')
                  .select('first_name, last_name, profile_picture')
                  .eq('id', meeting.sales_rep_id)
                  .single();

                if (salesRepData && salesRepData.first_name) {
                  salesRepName = `${salesRepData.first_name} ${salesRepData.last_name || ''}`.trim();
                  salesRepProfilePicture = salesRepData.profile_picture;
                }
              } catch (error) {
                logger.error('Error fetching sales rep data', { error: error.message });
              }
            }

            return {
              ...meeting,
              date: meeting.meeting_date,
              time: meeting.start_time,
              salesRepId: meeting.sales_rep_id,
              customerId: meeting.customer_id,
              salesRep: salesRepName, // For customers, show sales rep name in EnhancedMeetingCard
              customer: salesRepName, // Keep for backward compatibility
              sales_rep_profile_picture: salesRepProfilePicture,
              restaurant: meeting.restaurant_name || meeting.restaurant || 'TBD',
              status: meeting.status || 'pending',
              orderTotal: meeting.actual_cost || meeting.estimated_cost || '0.00',
              notes: meeting.meeting_notes || meeting.notes || ''
            };
          }));

          setMeetings(transformedMeetings);
        } catch (error) {
          logger.error('Refresh (Customer): Error loading Supabase meetings', { error: error.message });
          // Fallback to localStorage
          const userMeetings = MeetingStorage.getMeetingsForUser(currentUser.id, userType);

          setMeetings(userMeetings);
        }
      }
    }
    setLoading(false);
  };

  const handleExportCalendar = () => {
    if (currentUser) {
      MeetingStorage.exportUserMeetingsICS(currentUser.id, currentUser.userType);
    }
  };

  // Get current date and boundaries
  const today = new Date();
  const todayStart = new Date(today);
  todayStart.setHours(0, 0, 0, 0);

  // Helper function to parse date strings as local dates (avoiding timezone issues)
  const parseLocalDate = (dateString) => {
    if (dateString.includes('-') && dateString.split('-').length === 3) {
      const [year, month, day] = dateString.split('-');
      return new Date(year, month - 1, day); // Month is 0-indexed
    } else {
      return new Date(dateString);
    }
  };

  // Helper function to check if a date has any blocked periods
  const getBlockedInfoForDate = (dateString) => {
    if (!blockedPeriods || blockedPeriods.length === 0) {
      return { isBlocked: false, isFullDay: false, reason: null };
    }

    const checkDate = new Date(dateString + 'T12:00:00');

    for (const period of blockedPeriods) {
      const startDate = new Date(period.startDate + 'T00:00:00');
      const endDate = new Date(period.endDate + 'T23:59:59');

      if (checkDate >= startDate && checkDate <= endDate) {
        return {
          isBlocked: true,
          isFullDay: period.allDay === true,
          reason: period.reason || 'Blocked',
          startTime: period.startTime,
          endTime: period.endTime
        };
      }
    }

    return { isBlocked: false, isFullDay: false, reason: null };
  };

  // Determine if user should see blocked periods (only customers and office staff)
  const currentUserType = currentUser?.userType || currentUser?.user_type;
  const shouldShowBlockedPeriods = currentUserType === 'customer' || currentUserType === 'office_staff';

  const formatDate = (dateString) => {
    // Use the same local date parsing logic
    const date = parseLocalDate(dateString);

    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);

    const todayCompare = new Date();
    todayCompare.setHours(0, 0, 0, 0);

    const tomorrow = new Date(todayCompare);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (compareDate.getTime() === todayCompare.getTime()) {
      return 'Today';
    } else if (compareDate.getTime() === tomorrow.getTime()) {
      return 'Tomorrow';
    } else {
      return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    }
  };

  // Filter meetings based on view type and status
  const getFilteredMeetings = () => {
    const baseDate = new Date(selectedDate);
    baseDate.setHours(0, 0, 0, 0);
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    let dateMeetings = [];

    if (viewType === 'daily') {
      const endDate = new Date(baseDate);
      endDate.setHours(23, 59, 59, 999);
      dateMeetings = meetings.filter(meeting => {
        const meetingDate = parseLocalDate(meeting.date);
        meetingDate.setHours(0, 0, 0, 0);
        return meetingDate >= baseDate && meetingDate <= endDate;
      });
    } else if (viewType === 'weekly') {
      // Get start of week (Monday)
      const startOfWeek = new Date(baseDate);
      const day = startOfWeek.getDay();
      const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
      startOfWeek.setDate(diff);
      startOfWeek.setHours(0, 0, 0, 0);

      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      endOfWeek.setHours(23, 59, 59, 999);

      dateMeetings = meetings.filter(meeting => {
        const meetingDate = parseLocalDate(meeting.date);
        meetingDate.setHours(0, 0, 0, 0);
        return meetingDate >= startOfWeek && meetingDate <= endOfWeek;
      });
    } else if (viewType === 'monthly') {
      // If a day is clicked in the calendar, filter to that specific day
      if (clickedDay) {
        const clickedDayStart = new Date(clickedDay);
        clickedDayStart.setHours(0, 0, 0, 0);
        const clickedDayEnd = new Date(clickedDay);
        clickedDayEnd.setHours(23, 59, 59, 999);

        dateMeetings = meetings.filter(meeting => {
          const meetingDate = parseLocalDate(meeting.date);
          meetingDate.setHours(0, 0, 0, 0);
          return meetingDate >= clickedDayStart && meetingDate <= clickedDayEnd;
        });
      } else {
        // No day clicked - show all meetings for the month
        const startOfMonth = new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);
        const endOfMonth = new Date(baseDate.getFullYear(), baseDate.getMonth() + 1, 0);
        endOfMonth.setHours(23, 59, 59, 999);

        dateMeetings = meetings.filter(meeting => {
          const meetingDate = parseLocalDate(meeting.date);
          meetingDate.setHours(0, 0, 0, 0);
          return meetingDate >= startOfMonth && meetingDate <= endOfMonth;
        });
      }
    } else if (viewType === 'yearly') {
      const startOfYear = new Date(baseDate.getFullYear(), 0, 1);
      const endOfYear = new Date(baseDate.getFullYear(), 11, 31);
      endOfYear.setHours(23, 59, 59, 999);

      dateMeetings = meetings.filter(meeting => {
        const meetingDate = parseLocalDate(meeting.date);
        meetingDate.setHours(0, 0, 0, 0);
        return meetingDate >= startOfYear && meetingDate <= endOfYear;
      });
    }

    // Auto-mark past confirmed meetings as completed BEFORE applying status filter
    const autoCompletedMeetings = dateMeetings.map(meeting => {
      const meetingDate = parseLocalDate(meeting.date);
      const meetingDateTime = new Date(meetingDate.toDateString() + ' ' + meeting.time);

      // If meeting was confirmed and the date/time has passed, mark as completed
      if (meeting.status === 'confirmed' && meetingDateTime < now) {
        return { ...meeting, status: 'completed' };
      }
      return meeting;
    });

    // Apply status filter AFTER auto-completion
    if (statusFilter === 'all') {
      return autoCompletedMeetings;
    }

    return autoCompletedMeetings.filter(meeting => {
      const meetingDate = parseLocalDate(meeting.date);
      meetingDate.setHours(0, 0, 0, 0);
      const isFuture = meetingDate >= now;

      if (statusFilter === 'confirmed') {
        // Show future confirmed meetings
        return meeting.status === 'confirmed' && isFuture;
      } else if (statusFilter === 'active-pending') {
        // Show both confirmed and pending future meetings
        return (meeting.status === 'confirmed' || meeting.status === 'pending') && isFuture;
      } else if (statusFilter === 'pending') {
        // Show future pending meetings
        return meeting.status === 'pending' && isFuture;
      } else if (statusFilter === 'completed') {
        // Show completed meetings (all dates in the selected interval)
        return meeting.status === 'completed';
      } else if (statusFilter === 'cancelled') {
        // Show cancelled meetings (all dates in the selected interval)
        return meeting.status === 'cancelled';
      }

      return false;
    });
  };

  // Get filtered meetings with auto-completion applied
  const filteredMeetings = getFilteredMeetings();

  // Organize meetings by priority: today's first, then upcoming, then completed, then cancelled
  const organizedMeetings = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayEnd = new Date(today);
    todayEnd.setHours(23, 59, 59, 999);

    // Separate meetings into categories - make them mutually exclusive
    const todaysMeetings = filteredMeetings.filter(meeting => {
      const meetingDate = parseLocalDate(meeting.date);
      meetingDate.setHours(0, 0, 0, 0);
      return meetingDate.getTime() === today.getTime() && meeting.status !== 'completed' && meeting.status !== 'cancelled';
    });

    const upcomingMeetings = filteredMeetings.filter(meeting => {
      const meetingDate = parseLocalDate(meeting.date);
      meetingDate.setHours(0, 0, 0, 0);
      return meetingDate > today && meeting.status !== 'completed' && meeting.status !== 'cancelled';
    });

    const completedMeetings = filteredMeetings.filter(meeting => {
      const meetingDate = parseLocalDate(meeting.date);
      meetingDate.setHours(0, 0, 0, 0);
      return (meetingDate < today || meeting.status === 'completed') && meeting.status !== 'cancelled';
    });

    const cancelledMeetings = filteredMeetings.filter(meeting => {
      return meeting.status === 'cancelled';
    });

    // Sort each category by date/time
    const sortByDateTime = (meetings) => {
      return meetings.sort((a, b) => {
        const dateA = new Date(parseLocalDate(a.date).toDateString() + ' ' + a.time);
        const dateB = new Date(parseLocalDate(b.date).toDateString() + ' ' + b.time);
        return dateA - dateB;
      });
    };

    // Sort completed meetings by most recent first (reverse chronological)
    const sortCompletedByDateTime = (meetings) => {
      return meetings.sort((a, b) => {
        const dateA = new Date(parseLocalDate(a.date).toDateString() + ' ' + a.time);
        const dateB = new Date(parseLocalDate(b.date).toDateString() + ' ' + b.time);
        return dateB - dateA; // Reversed for most recent first
      });
    };

    // Combine in priority order: pending/confirmed first, then completed, then cancelled at bottom
    return [
      ...sortByDateTime(todaysMeetings),
      ...sortByDateTime(upcomingMeetings),
      ...sortCompletedByDateTime(completedMeetings),
      ...sortByDateTime(cancelledMeetings)
    ];
  };

  // Get date range text for display
  const getDateRangeText = () => {
    const baseDate = new Date(selectedDate);
    
    if (viewType === 'daily') {
      return baseDate.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } else if (viewType === 'weekly') {
      const startOfWeek = new Date(baseDate);
      const day = startOfWeek.getDay();
      const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
      startOfWeek.setDate(diff);
      
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      
      return `${startOfWeek.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endOfWeek.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    } else if (viewType === 'monthly') {
      return baseDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    } else if (viewType === 'yearly') {
      return baseDate.toLocaleDateString('en-US', { year: 'numeric' });
    }
    return '';
  };

  // Navigation functions
  const navigateDate = (direction) => {
    const newDate = new Date(selectedDate);

    if (viewType === 'daily') {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
    } else if (viewType === 'weekly') {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
    } else if (viewType === 'monthly') {
      newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
      // Clear clicked day when navigating to a different month
      setClickedDay(null);
    } else if (viewType === 'yearly') {
      newDate.setFullYear(newDate.getFullYear() + (direction === 'next' ? 1 : -1));
    }

    setSelectedDate(newDate);
  };

  // Get all upcoming meetings for stats
  const upcomingMeetings = meetings.filter(meeting => {
    const meetingDate = parseLocalDate(meeting.date);
    meetingDate.setHours(0, 0, 0, 0);
    return meetingDate >= todayStart;
  });

  // Calendar helper function to generate calendar grid
  const getCalendarDays = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    // Create array for calendar grid (6 weeks × 7 days = 42 cells)
    const calendarDays = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      calendarDays.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      calendarDays.push(new Date(year, month, day));
    }

    // Fill remaining cells to complete the grid
    while (calendarDays.length < 42) {
      calendarDays.push(null);
    }

    return calendarDays;
  };

  // Get meeting status for a specific date
  const getMeetingsForDate = (dateString) => {
    return meetings.filter(meeting => {
      // Check both 'date' and 'meeting_date' fields for compatibility
      // Some meetings may have date, others meeting_date depending on source
      const meetingDate = meeting.date || meeting.meeting_date;
      return meetingDate === dateString;
    });
  };

  // Get meetings for a specific month (for year view)
  const getMeetingsForMonth = (year, month) => {
    return meetings.filter(meeting => {
      const meetingDate = parseLocalDate(meeting.date);
      return meetingDate.getFullYear() === year && meetingDate.getMonth() === month;
    });
  };

  return (
    <>
    <PullToRefresh onRefresh={refreshMeetings} disabled={loading}>
    <div className="p-3 sm:p-6 max-w-6xl mx-auto">
      {/* Page Title */}
      <div className="mb-4 sm:mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Calendar</h1>
        <p className="text-gray-600 text-sm sm:text-base">Manage your schedule and view all upcoming meetings</p>
      </div>

      {/* Calendar Visual Grid */}
      <div className="border border-gray-200 rounded-xl bg-white shadow-lg overflow-hidden w-full">
        {/* Calendar Header with Navigation - Compact */}
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 border-b border-gray-200 px-2 py-2 sm:p-3">
          <div className="flex items-center justify-between gap-1">
            {/* View selector */}
            <select
              value={viewType}
              onChange={(e) => setViewType(e.target.value)}
              className="px-2 py-1.5 text-xs sm:text-sm font-medium border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 no-touch-target"
            >
              <option value="daily">Day</option>
              <option value="weekly">Week</option>
              <option value="monthly">Month</option>
              <option value="yearly">Year</option>
            </select>

            {/* Navigation */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => navigateDate('prev')}
                className="p-1.5 hover:bg-blue-200 rounded-lg transition-colors touch-manipulation"
              >
                <ChevronRight className="w-5 h-5 rotate-180 text-gray-700" />
              </button>

              <h3 className="text-xs sm:text-base font-semibold text-gray-800 text-center min-w-[100px] sm:min-w-[140px]">{getDateRangeText()}</h3>

              <button
                onClick={() => navigateDate('next')}
                className="p-1.5 hover:bg-blue-200 rounded-lg transition-colors touch-manipulation"
              >
                <ChevronRight className="w-5 h-5 text-gray-700" />
              </button>
            </div>

            {/* Today button */}
            <button
              onClick={() => {
                setSelectedDate(new Date());
                if (viewType === 'monthly') {
                  setClickedDay(new Date());
                }
              }}
              className="px-2 sm:px-3 py-1.5 text-xs sm:text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors touch-manipulation no-touch-target"
            >
              Today
            </button>
          </div>
        </div>

        {viewType === 'yearly' ? (
          /* Year View - Grid of 12 Months - Mobile optimized */
          <div className="p-3 sm:p-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-4">
              {[...Array(12)].map((_, monthIndex) => {
                const monthDate = new Date(selectedDate.getFullYear(), monthIndex, 1);
                const monthMeetings = getMeetingsForMonth(selectedDate.getFullYear(), monthIndex);
                const confirmedCount = monthMeetings.filter(m => m.status === 'confirmed').length;
                const pendingCount = monthMeetings.filter(m => m.status === 'pending').length;
                const totalCount = monthMeetings.length;

                let bgClass = 'bg-white hover:bg-gray-50';
                if (totalCount > 0) {
                  if (confirmedCount > 0 && pendingCount === 0) {
                    bgClass = 'bg-green-50 hover:bg-green-100';
                  } else if (pendingCount > 0 && confirmedCount === 0) {
                    bgClass = 'bg-yellow-50 hover:bg-yellow-100';
                  } else if (confirmedCount > 0 && pendingCount > 0) {
                    bgClass = 'bg-orange-50 hover:bg-orange-100';
                  }
                }

                const isCurrentMonth = new Date().getFullYear() === selectedDate.getFullYear() && new Date().getMonth() === monthIndex;

                return (
                  <button
                    key={monthIndex}
                    onClick={() => {
                      setSelectedDate(new Date(selectedDate.getFullYear(), monthIndex, 1));
                      setViewType('monthly');
                    }}
                    className={`p-2 sm:p-4 border-2 rounded-lg transition-all touch-manipulation ${bgClass} ${
                      isCurrentMonth ? 'border-purple-500 shadow-lg' : 'border-gray-200'
                    }`}
                    style={{ minHeight: '80px' }}
                  >
                    <div className="text-center">
                      <p className="font-semibold text-gray-800 text-sm sm:text-base mb-1">
                        {monthDate.toLocaleDateString('en-US', { month: 'short' })}
                      </p>
                      {totalCount > 0 ? (
                        <div className="space-y-0.5">
                          <p className="text-xl sm:text-2xl font-bold text-gray-900">{totalCount}</p>
                          <p className="text-[10px] sm:text-xs text-gray-600">
                            meeting{totalCount !== 1 ? 's' : ''}
                          </p>
                          <div className="flex flex-wrap justify-center gap-0.5 sm:gap-1 mt-1">
                            {confirmedCount > 0 && (
                              <span className="px-1 sm:px-2 py-0.5 text-[9px] sm:text-xs bg-green-200 text-green-800 rounded">
                                {confirmedCount}
                              </span>
                            )}
                            {pendingCount > 0 && (
                              <span className="px-1 sm:px-2 py-0.5 text-[9px] sm:text-xs bg-yellow-200 text-yellow-800 rounded">
                                {pendingCount}
                              </span>
                            )}
                          </div>
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400 mt-2">No meetings</p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <>
            {/* Days of Week Header */}
            <div className="grid grid-cols-7 border-b border-gray-200 w-full">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="p-2 md:p-3 text-center text-xs font-medium text-gray-600 bg-gray-50 min-w-0">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Days Grid - Color-coded by meeting status */}
            <div className="grid grid-cols-7 w-full">
            {getCalendarDays(selectedDate).map((day, index) => {
              if (!day) {
                return <div key={index} className="h-12 sm:h-16 md:h-20 min-w-0"></div>;
              }

              // Format date in local timezone (YYYY-MM-DD) to match meeting.date from database
              const dateString = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
              const dayMeetings = getMeetingsForDate(dateString);

              // Check if this date has blocked periods
              const blockedInfo = getBlockedInfoForDate(dateString);

              // Get today's date in local timezone (YYYY-MM-DD format)
              const todayDate = new Date();
              const todayString = `${todayDate.getFullYear()}-${String(todayDate.getMonth() + 1).padStart(2, '0')}-${String(todayDate.getDate()).padStart(2, '0')}`;
              const isToday = dateString === todayString;

              const isPast = day < new Date(new Date().toDateString());

              // Count meetings by status
              const confirmedCount = dayMeetings.filter(m => m.status === 'confirmed').length;
              const pendingCount = dayMeetings.filter(m => m.status === 'pending').length;
              const completedCount = dayMeetings.filter(m => m.status === 'completed').length;
              const totalCount = dayMeetings.length;

              // Determine background color based on meetings and blocked status
              let bgClass = 'bg-white hover:bg-gray-50';
              let textClass = 'text-gray-900';

              if (isPast) {
                // Past days - show gray background but still indicate if there were meetings
                if (completedCount > 0 || totalCount > 0) {
                  bgClass = 'bg-blue-50 hover:bg-blue-100';
                  textClass = 'text-blue-700';
                } else {
                  bgClass = 'bg-gray-50';
                  textClass = 'text-gray-400';
                }
              } else if (shouldShowBlockedPeriods && blockedInfo.isBlocked && blockedInfo.isFullDay) {
                // Full day blocked - show with striped/red indicator (only for customers/office staff)
                bgClass = 'bg-red-50 hover:bg-red-100';
                textClass = 'text-red-700';
              } else if (totalCount > 0) {
                if (confirmedCount > 0 && pendingCount === 0) {
                  // All confirmed - green
                  bgClass = 'bg-green-100 hover:bg-green-150';
                  textClass = 'text-green-900';
                } else if (pendingCount > 0 && confirmedCount === 0) {
                  // All pending - yellow
                  bgClass = 'bg-yellow-100 hover:bg-yellow-150';
                  textClass = 'text-yellow-900';
                } else if (confirmedCount > 0 && pendingCount > 0) {
                  // Mix of confirmed and pending - orange
                  bgClass = 'bg-orange-100 hover:bg-orange-150';
                  textClass = 'text-orange-900';
                }
              } else if (shouldShowBlockedPeriods && blockedInfo.isBlocked) {
                // Partial day blocked (time-specific) - show with lighter indicator (only for customers/office staff)
                bgClass = 'bg-red-50/50 hover:bg-red-100/50';
                textClass = 'text-red-600';
              }

              if (isToday) {
                textClass += ' font-bold';
              }

              const isClickedDay = clickedDay && clickedDay.toDateString() === day.toDateString();

              // Check if day is in selected week (for weekly view)
              const isInSelectedWeek = viewType === 'weekly' && (() => {
                const weekStart = new Date(selectedDate);
                const dayOfWeek = weekStart.getDay();
                const diff = weekStart.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
                weekStart.setDate(diff);
                weekStart.setHours(0, 0, 0, 0);

                const weekEnd = new Date(weekStart);
                weekEnd.setDate(weekStart.getDate() + 6);
                weekEnd.setHours(23, 59, 59, 999);

                const dayTime = new Date(day).setHours(0, 0, 0, 0);
                return dayTime >= weekStart.getTime() && dayTime <= weekEnd.getTime();
              })();

              // Check if user is customer/office_staff for modal logic
              const isCustomerUser = currentUserType === 'customer' || currentUserType === 'office_staff';

              return (
                <button
                  key={index}
                  onClick={() => {
                    // For customers: show quick view modal for any day with meetings
                    if (isCustomerUser && totalCount > 0) {
                      // Sort meetings by time (earliest first)
                      const sortedDayMeetings = [...dayMeetings].sort((a, b) => {
                        const timeA = a.start_time || a.time || '00:00';
                        const timeB = b.start_time || b.time || '00:00';
                        return timeA.localeCompare(timeB);
                      });

                      // Store all meetings for navigation in modal
                      setSelectedDayMeetings(sortedDayMeetings);
                      // Set first meeting as the initially selected one (for backward compatibility)
                      setSelectedRequestMeeting(sortedDayMeetings[0]);
                      setShowRequestModal(true);
                      return; // Don't change the view
                    }

                    // Default calendar navigation behavior
                    if (viewType === 'daily') {
                      // In daily view, clicking a day updates both selectedDate and clickedDay
                      setSelectedDate(new Date(day));
                      setClickedDay(new Date(day));
                    } else if (viewType === 'weekly') {
                      // In weekly view, clicking a day updates selectedDate to that week
                      setSelectedDate(new Date(day));
                    } else if (viewType === 'monthly') {
                      // In monthly view, clicking a day switches to daily view for that day
                      setSelectedDate(new Date(day));
                      setClickedDay(new Date(day));
                      setViewType('daily');
                    } else {
                      // For other views (yearly), clicking sets the day
                      setClickedDay(new Date(day));
                    }
                  }}
                  className={`group h-12 sm:h-16 md:h-20 text-xs sm:text-sm font-medium border-r border-b border-gray-200 transition-all duration-200 relative p-1 sm:p-2 min-w-0 ${bgClass} ${textClass} ${
                    isToday ? 'ring-2 ring-inset ring-blue-600 shadow-md' : ''
                  } ${isClickedDay ? 'ring-2 ring-inset ring-purple-500 shadow-lg' : ''} ${isInSelectedWeek ? 'ring-2 ring-inset ring-purple-500 shadow-lg' : ''}`}
                >
                  <div className="flex flex-col items-center justify-center h-full min-w-0">
                    <span className="block">{day.getDate()}</span>
                    {/* Status dots - all centered together */}
                    {(totalCount > 0 || (shouldShowBlockedPeriods && blockedInfo.isBlocked && !isPast)) && (
                      <div className="flex gap-0.5 sm:gap-1 mt-0.5 sm:mt-1 flex-wrap justify-center max-w-full">
                        {/* Render one dot per confirmed meeting */}
                        {[...Array(confirmedCount)].map((_, i) => (
                          <div key={`confirmed-${i}`} className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-green-600 rounded-full flex-shrink-0" title={`${confirmedCount} confirmed`}></div>
                        ))}
                        {/* Render one dot per pending meeting */}
                        {[...Array(pendingCount)].map((_, i) => (
                          <div key={`pending-${i}`} className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-yellow-600 rounded-full flex-shrink-0" title={`${pendingCount} pending`}></div>
                        ))}
                        {/* Render one dot per completed meeting */}
                        {[...Array(completedCount)].map((_, i) => (
                          <div key={`completed-${i}`} className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-blue-600 rounded-full flex-shrink-0" title={`${completedCount} completed`}></div>
                        ))}
                        {/* Blocked indicator dot - same size and position as other dots */}
                        {shouldShowBlockedPeriods && blockedInfo.isBlocked && !isPast && (
                          <div
                            className={`w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full flex-shrink-0 ${blockedInfo.isFullDay ? 'bg-red-500' : 'bg-red-300'}`}
                            title={blockedInfo.isFullDay
                              ? `Blocked: ${blockedInfo.reason}`
                              : `Partial block (${blockedInfo.startTime}-${blockedInfo.endTime}): ${blockedInfo.reason}`
                            }
                          ></div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Add Meeting button - only for customers/office_staff on future dates */}
                  {isCustomerUser && !isPast && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddMeetingManually(dateString);
                      }}
                      className="absolute bottom-0.5 right-0.5 w-4 h-4 sm:w-5 sm:h-5 bg-green-500 hover:bg-green-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                      title="Add meeting manually"
                    >
                      <Plus className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                    </button>
                  )}
                </button>
              );
            })}
          </div>

            {/* Calendar Legend */}
            <div className="border-t border-gray-200 p-4 bg-gray-50">
              <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs">
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-green-100 border border-green-300 rounded"></div>
                  <span className="text-gray-700">Confirmed</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-yellow-100 border border-yellow-300 rounded"></div>
                  <span className="text-gray-700">Pending</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-orange-100 border border-orange-300 rounded"></div>
                  <span className="text-gray-700">Mixed</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-blue-100 border border-blue-300 rounded"></div>
                  <span className="text-gray-700">Completed</span>
                </div>
                {shouldShowBlockedPeriods && (
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 bg-white border border-gray-300 rounded flex items-center justify-center">
                      <div className="w-1.5 h-1.5 bg-red-500 rounded-full"></div>
                    </div>
                    <span className="text-gray-700">Blocked</span>
                  </div>
                )}
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-white border border-gray-300 rounded"></div>
                  <span className="text-gray-700">No Meetings</span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Filter and Summary Section - Below calendar */}
      <div className="mt-4 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Calendar className="w-4 h-4 text-blue-600" />
          <span className="font-medium">{filteredMeetings.length} meeting{filteredMeetings.length !== 1 ? 's' : ''}</span>
          <span className="text-gray-400">|</span>
          <span className="text-green-600">{filteredMeetings.filter(m => m.status === 'confirmed').length} confirmed</span>
          <span className="text-yellow-600">{filteredMeetings.filter(m => m.status === 'pending').length} pending</span>
        </div>

        {/* Status Filter */}
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-2 py-1.5 text-xs sm:text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 no-touch-target"
        >
          <option value="all">All</option>
          <option value="confirmed">Confirmed</option>
          <option value="active-pending">Active + Pending</option>
          <option value="pending">Pending</option>
          <option value="cancelled">Cancelled</option>
          <option value="completed">Completed</option>
        </select>
      </div>

      {/* Meeting List Section */}
      <div className="mt-4">
        {/* Meetings Grid */}
        {filteredMeetings.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 w-full">
            {organizedMeetings().map(meeting => (
              <EnhancedMeetingCard
                key={meeting.id}
                meeting={meeting}
                userType={currentUser?.userType || currentUser?.user_type || 'sales'}
                onClick={() => {
                  // Always go to detail view when clicking a meeting tile
                  setSelectedMeetingId(meeting.id);
                  setCurrentView('meeting-detail');
                }}
              />
            ))}
          </div>
        ) : (
          <div>
            {/* Show available favorites card if there are any */}
            {availableFavorites.length > 0 && clickedDay && (
              <AvailableFavoritesCard
                availableProviders={availableFavorites}
                selectedDate={clickedDay}
                onScheduleNow={handleScheduleNow}
              />
            )}

            {/* No meetings message */}
            <div className="bg-gray-50 rounded-lg p-12 text-center">
              <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h4 className="text-lg font-medium text-gray-600 mb-2">No Meetings</h4>
              <p className="text-gray-500 mb-4">
                {clickedDay ? 'No meetings scheduled for this day' : 'No meetings found with the selected filters'}
              </p>
              {loadingAvailability && (
                <p className="text-gray-400 text-sm mb-2">Checking favorite providers' availability...</p>
              )}
              <button
                onClick={() => setCurrentView('find')}
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                Find Healthcare Providers →
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Quick Stats - Updated based on selected view */}
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 w-full max-w-full">
        <div className="bg-blue-50 rounded-lg p-3 md:p-4 min-w-0">
          <p className="text-xs sm:text-sm text-blue-600 mb-1 truncate">
            {viewType === 'daily' ? 'Today' : viewType === 'weekly' ? 'This Week' : viewType === 'monthly' ? 'This Month' : 'This Year'}
          </p>
          <p className="text-xl sm:text-2xl font-bold text-blue-900 truncate">
            {filteredMeetings.length} meeting{filteredMeetings.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="bg-green-50 rounded-lg p-3 md:p-4 min-w-0">
          <p className="text-xs sm:text-sm text-green-600 mb-1 truncate">Unique Reps</p>
          <p className="text-xl sm:text-2xl font-bold text-green-900 truncate">
            {new Set(filteredMeetings.map(m => m.salesRepId || m.sales_rep_id).filter(Boolean)).size}
          </p>
        </div>
        <div className="bg-purple-50 rounded-lg p-3 md:p-4 min-w-0">
          <p className="text-xs sm:text-sm text-purple-600 mb-1 truncate">Companies</p>
          <p className="text-xl sm:text-2xl font-bold text-purple-900 truncate">
            {new Set(filteredMeetings.map(m => {
              // Extract company from salesRepData or fallback fields
              if (m.salesRepData?.company) return m.salesRepData.company;
              if (m.sales_rep_company) return m.sales_rep_company;
              if (m.salesRepCompany) return m.salesRepCompany;
              return null;
            }).filter(Boolean)).size}
          </p>
        </div>
      </div>
    </div>
    </PullToRefresh>

    {/* Meeting Quick View Modal - for customers to view meeting details and approve/decline pending */}
    <MeetingQuickViewModal
      meeting={selectedRequestMeeting}
      meetings={selectedDayMeetings}
      isOpen={showRequestModal}
      onClose={() => {
        setShowRequestModal(false);
        setSelectedRequestMeeting(null);
        setSelectedDayMeetings([]);
      }}
      onApprove={(meeting) => {
        // Refresh meetings after approval
        refreshMeetings();
      }}
      onDecline={(meeting) => {
        // Refresh meetings after decline
        refreshMeetings();
      }}
      onViewDetails={(meeting) => {
        setSelectedMeetingId(meeting.id);
        setCurrentView('meeting-detail');
      }}
      onAddMeetingManually={handleAddMeetingManually}
      approvedReps={approvedReps}
      currentUser={currentUser}
    />
    </>
  );
};

export default MeetingsView;